import {translate} from '../../../plugins/i18n/i18n';

const DEFAULT_TABLE_MAX_WIDTH = '1fr';

// Type for the header key: either a string or a structured object with message and payload
export type TableHeader =
  | string
  | {message: string; payload?: Record<string, unknown>};

/** Interface to define all possible options when attribute is displayed inside table */
export interface TableProperties {
  header?: TableHeader | null;
  sharable?: boolean | null;
  minWidth?: string | null;
  maxWidth?: string | null; // When not specified -> default to 1fr
  readOnly?: boolean | null;
  hideByDefault?: boolean | null;
  /** Hide this column in the table; show only in the edition sidebar. */
  onlyShowInEdition?: boolean | null;
  /** Hide this field in the edition sidebar when the row has no linked resource. */
  hideInEditionUnlessHasResource?: boolean | null;
  /** Hide this field in the edition sidebar when the row's itemType is one of these values (e.g. COMMENT, TITLE). */
  hideInEditionForItemTypes?: string[] | null;
}

/** Interface to define all base options that are common to all attributes */
export interface BaseAttributeOptions {
  /** Attribute key in the model’s `allAttributes` map; defaults to `''` if omitted. */
  name?: string;
  column?: string | null;
  creationValue?: unknown | null;
  validations?: Record<string, unknown> | null;
  validationGroup?: string | null;
  transform?: ((arg: unknown) => unknown) | null;
  readOnly?: boolean | null;
  formLocation?: string | null;
  tableProperties?: TableProperties | null;
  forcedType?: string | null;
  formItemName?: string | null;
  moduleUsage?: string | null;
  notFetchedFilters?: Record<string, unknown> | null;
  /** Value written to the clipboard for this column. Receives the row. */
  copyValue?: ((item: Record<string, unknown>) => unknown) | null;
}

/** Base attribute that is the parent class for all other attribute types */
export class BaseAttribute {
  name: string = '';
  column: string | null = null;
  creationValue: unknown | null = null;
  validations: Record<string, unknown> | null = {};
  validationGroup: string | null = null;
  transform: ((arg: unknown) => unknown) | null = null;
  readOnly?: boolean | null;
  formLocation?: string | null;
  tableProperties: TableProperties | null = null;
  forcedType?: string | null;
  formItemName?: string | null;
  moduleUsage?: string | null;
  notFetchedFilters?: Record<string, unknown> | null;
  copyValue?: ((item: Record<string, unknown>) => unknown) | null;

  constructor(options: BaseAttributeOptions = {}) {
    const {
      name = '',
      column = null,
      creationValue = null,
      validations = {},
      validationGroup = null,
      transform = null,
      readOnly = false,
      formLocation = null,
      tableProperties = null,
      forcedType = null,
      formItemName = null,
      moduleUsage = null,
      notFetchedFilters = null,
      copyValue = null,
    } = options;

    this.name = name;
    this.column = column;
    this.creationValue = creationValue;
    this.validations = validations || {};
    this.validationGroup = validationGroup || null;
    this.transform = transform;
    this.readOnly = readOnly;
    this.formLocation = formLocation;
    this.forcedType = forcedType;
    this.formItemName = formItemName;
    this.moduleUsage = moduleUsage;
    this.notFetchedFilters = notFetchedFilters ?? null;
    this.copyValue = copyValue;

    // Fallback to default max width if not specified
    this.tableProperties = tableProperties
      ? {
          ...tableProperties,
          maxWidth: tableProperties.maxWidth ?? DEFAULT_TABLE_MAX_WIDTH,
        }
      : null;
  }

  // Input type that is used if attribute is used in edit mode
  get inputType(): string {
    return this.forcedType ?? 'standard';
  }

  // Custom table properties that are passed for the attribute (need to be defined in the corresponding attribute class)
  get customTableProperties(): Object {
    return {};
  }

  // Returns the full table properties needed to build the column in the HitTable component
  get fullTableProperties(): Object | null {
    // If table properties are not set or if the header is not set, the column is not displayed in the table
    const headerKey: TableHeader | null = this.tableProperties?.header ?? null;
    if (!headerKey) return null;

    // Resolve the header and tooltip string to display for the column
    let resolvedHeader: string;
    let resolvedTooltip: string | null = null;
    if (typeof headerKey === 'string') {
      resolvedHeader = translate(headerKey);
      resolvedTooltip = translate(headerKey.replace('-short', ''));
    } else {
      resolvedHeader = translate(headerKey.message, headerKey.payload);
      resolvedTooltip = null;
    }

    // Return the full table properties object to build the column in the UI
    return {
      ...this.tableProperties,
      ...this.customTableProperties,
      propertyName: this.name,
      column: this.column,
      header: resolvedHeader,
      'header-tooltip': resolvedTooltip,
      moduleUsage: this.moduleUsage,
      notFetchedFilters: this.notFetchedFilters,
      type: this.forcedType ?? this.inputType,
      hasForcedType: this.forcedType !== null,
      formItemName: this.formItemName,
      copyValue: this.copyValue,
    };
  }

  /** The name of the column in the database to use for GET and POST requests */
  get dbColumn(): string {
    return this.column ?? this.name;
  }

  /** String used to query the data via postgrest */
  get selectString(): string {
    if (!this.column || this.column == this.name) return this.name;
    return `${this.name}:${this.column ?? this.name}`;
  }

  /** Initial value used for this attribute when a new record is created */
  getInitialValue(): unknown {
    if (this.creationValue !== undefined && this.creationValue !== null) {
      return this.creationValue;
    }
    return undefined;
  }

  /**
   * Converts the value from the UI input to the value type that is sent to the database
   * If specific conversion is needed, it can be overridden in the corresponding attribute class
   * Returns two values:
   * - The value sent to the database
   * - The value to update the data in the UI
   */
  toDbValue(value: unknown): [unknown, unknown] {
    return [value, value];
  }

  /** Converts the value from the database to a value that can be used in the UI */
  toLocalValue(item: Record<string, unknown>): unknown {
    const attValue = item[this.name];
    if (this.transform) return this.transform(attValue);
    return attValue;
  }
}
